import type { Table } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface DataTablePaginationProps<TData> extends React.ComponentProps<"div"> {
  table: Table<TData>;
  pageSizeOptions?: number[];
  showSelectedRowCount?: boolean;
  totalRecords?: number | null;
}

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [5, 10, 20, 30, 40, 50],
  className,
  showSelectedRowCount = false,
  totalRecords,
  ...props
}: DataTablePaginationProps<TData>) {
  // Fallback to table's filtered row count when totalRecords is null
  const effectiveTotalRecords =
    totalRecords ??
    Math.max(
      table.getFilteredRowModel().rows.length,
      table.getState().pagination.pageIndex *
        table.getState().pagination.pageSize +
        0,
    );

  const pageRange = `${Math.min(
    table.getState().pagination.pageIndex *
      table.getState().pagination.pageSize +
      1,
    effectiveTotalRecords,
  )} - ${Math.min(
    table.getState().pagination.pageSize *
      (table.getState().pagination.pageIndex + 1),
    effectiveTotalRecords,
  )}`;

  return (
    <div
      className={cn(
        "flex w-full flex-col-reverse gap-3 overflow-auto p-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4 lg:gap-8 items-center",
        className,
        showSelectedRowCount ? "sm:justify-between" : "sm:justify-end",
      )}
      {...props}
    >
      {showSelectedRowCount ? (
        <div className="text-muted-foreground flex-1 text-md whitespace-nowrap">
          <>
            {table.getFilteredSelectedRowModel().rows.length} of{" "}
            {table.getFilteredRowModel().rows.length} row(s) selected.
          </>
        </div>
      ) : (
        <></>
      )}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:gap-4 lg:gap-6">
        <div className="flex items-center justify-center space-x-2 sm:justify-start">
          <p className="text-md font-medium mb-0">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[4.5rem] [&[data-size]]:h-8 gap-1">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top" className="bg-white min-w-[1.5rem]">
              {pageSizeOptions.map((pageSize) => (
                <SelectItem
                  key={pageSize}
                  value={`${pageSize}`}
                  className="cursor-pointer hover:bg-gray-200 w-fit"
                >
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center text-md font-medium flex-shrink-0 order-first sm:order-none text-center sm:text-left">
          <span className="hidden xs:inline mr-1">{pageRange}</span>
          {totalRecords != null ? <> of {totalRecords}</> : null}
        </div>

        <div className="flex items-center justify-center space-x-2">
          <Button
            aria-label="Go to first page"
            variant="outline"
            size="icon"
            className="hidden size-8 md:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            aria-label="Go to previous page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            aria-label="Go to next page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            aria-label="Go to last page"
            variant="outline"
            size="icon"
            className="hidden size-8 md:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
